var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { withTheme } from 'emotion-theming';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { paddingForAspectRatio } from './utils/paddingForAspectRatio';
import t from '@jetshop/intl';
import { useConstructImage } from './useConstructImage';
export const transparentDataImg = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
const defaultImageState = {
    imgLoaded: false,
    error: false
};
const loadedImageState = {
    imgLoaded: true,
    error: false
};
let hydrated = false;
const imageCache = Object.create({});
function isInCache(src) {
    return src in imageCache;
}
function cacheImage(src) {
    imageCache[src] = true;
}
function imageStateReducer(_, action) {
    switch (action.type) {
        case 'loaded':
            cacheImage(action.cacheKey);
            return loadedImageState;
        case 'error':
            return { imgLoaded: false, error: true };
        default:
            console.error(`Action ${action.type} is invalid for image reducer`);
            return;
    }
}
function useObjectFitPolyfill(imageRefs) {
    const [polyfilled, setPolyfilled] = React.useState(false);
    React.useEffect(() => {
        const testImg = document.createElement(`img`);
        if (typeof testImg.style.objectFit === `undefined` ||
            typeof testImg.style.objectPosition === `undefined`) {
            import(`object-fit-images`).then(({ default: ObjectFitImages }) => {
                imageRefs.forEach(imageRef => {
                    imageRef.current && ObjectFitImages(imageRef.current);
                });
                setPolyfilled(true);
            });
        }
    }, [imageRefs]);
    return polyfilled;
}
const Image = function Image(_a) {
    var { src: originalSrc, cover = false, alt = '', crop = false, sizes: sizesArray = [1], fillAvailableSpace = false, gravity, aspect = null, theme, children, quality, error: ErrorComp = Error, className, critical } = _a, rest = __rest(_a, ["src", "cover", "alt", "crop", "sizes", "fillAvailableSpace", "gravity", "aspect", "theme", "children", "quality", "error", "className", "critical"]);
    // Set up the IntersectionObserver
    const [ref, inView] = useInView({ triggerOnce: true });
    // Check if the browser supports loading="lazy". In that case, we don't need
    // the IntersectionObserver
    const nativeLazyLoading = typeof HTMLImageElement !== 'undefined' &&
        'loading' in HTMLImageElement.prototype;
    const imageRef = React.createRef();
    const placeholderRef = React.createRef();
    useObjectFitPolyfill([imageRef, placeholderRef]);
    const [{ imgLoaded, error }, dispatch] = React.useReducer(imageStateReducer, critical ? loadedImageState : defaultImageState);
    const { src, hasFalsySrc, lqip, sizes, srcset, webpSrcset } = useConstructImage({
        gravity,
        crop,
        src: originalSrc,
        theme,
        sizes: sizesArray,
        aspect,
        quality,
        critical
    });
    const cacheKey = srcset;
    const handleImgLoad = React.useCallback(function handleImgLoad() {
        dispatch({ type: 'loaded', cacheKey });
    }, [cacheKey]);
    function handleImgError() {
        dispatch({ type: 'error' });
    }
    // Treat critical images as always cached
    const isCached = critical || isInCache(cacheKey);
    React.useEffect(() => {
        hydrated = true;
    }, []);
    React.useEffect(() => {
        // Handle case where image onLoad fires during SSR
        if (imageRef.current && imageRef.current.complete && !isCached) {
            handleImgLoad();
        }
    }, [handleImgLoad, imageRef, isCached]);
    // Render nothing if src is boolean
    if (typeof originalSrc === 'boolean' && !originalSrc) {
        return null;
    }
    const loaded = imgLoaded || hasFalsySrc || isCached;
    const objectFit = cover || fillAvailableSpace ? 'cover' : 'contain';
    const objectFitPolyfillStyles = { fontFamily: `"object-fit: ${objectFit}"` };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", Object.assign({ ref: 
            // If we have native lazyloading, by not applying the `ref` from
            // useInView we don't use IntersectionObserver at all
            nativeLazyLoading ? undefined : ref, "data-flight-image-container": "", "data-flight-image-loaded": loaded, className: className }, rest, { style: {
                position: `relative`,
                overflow: `hidden`,
                width: `100%`,
                height: fillAvailableSpace ? '100%' : 'auto',
                paddingBottom: fillAvailableSpace
                    ? 0
                    : !aspect
                        ? '100%'
                        : paddingForAspectRatio(aspect)
            } }),
            error ? (React.createElement(ErrorComp, null)) : (React.createElement(React.Fragment, null,
                React.createElement(Placeholder, { src: critical ? transparentDataImg : lqip || transparentDataImg, ref: placeholderRef, style: Object.assign({ objectFit, opacity: loaded ? 0 : 0.6, transitionDelay: `500ms` }, objectFitPolyfillStyles) }),
                (inView || nativeLazyLoading || !hydrated || isCached) && (React.createElement("picture", { "data-flight-image": "" },
                    React.createElement("source", { type: "image/webp", srcSet: webpSrcset, sizes: sizes }),
                    React.createElement("source", { srcSet: srcset, sizes: sizes }),
                    React.createElement(Img, { srcSet: srcset, src: src || transparentDataImg, sizes: sizes, onLoad: handleImgLoad, onError: handleImgError, alt: alt, ref: imageRef, style: Object.assign({ objectFit, opacity: loaded ? 1 : 0, transition: `opacity 500ms` }, objectFitPolyfillStyles) }))),
                React.createElement("noscript", null,
                    React.createElement("picture", null,
                        React.createElement(Img, { srcSet: srcset, src: src, sizes: sizes, alt: alt, style: {
                                objectFit
                            } }))))),
            children && (React.createElement("div", { "data-flight-image-children": "", style: {
                    position: 'relative',
                    zIndex: 2,
                    width: '100%',
                    height: '100%'
                } }, children)))));
};
export default withTheme(Image);
function Error() {
    return (React.createElement("div", { style: {
            background: '#e2e2e2',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: `absolute`,
            top: 0,
            left: 0,
            width: `100%`,
            height: `100%`
        } }, t('Image Not Found')));
}
const Placeholder = React.forwardRef((props, ref) => {
    return (React.createElement("picture", { "data-flight-image-placeholder": "" },
        React.createElement(Img, Object.assign({ loading: "auto", alt: "" }, props, { ref: ref }))));
});
const Img = React.forwardRef((props, ref) => {
    const { sizes, srcSet, src, style, alt, onLoad, onError, objectFit } = props, otherProps = __rest(props, ["sizes", "srcSet", "src", "style", "alt", "onLoad", "onError", "objectFit"]);
    const imgStyle = Object.assign({ position: `absolute`, top: 0, left: 0, width: `100%`, height: `100%`, objectPosition: `center`, objectFit }, style);
    return (React.createElement("img", Object.assign({ alt: alt, sizes: sizes, srcSet: srcSet, src: src, onLoad: onLoad, onError: onError, loading: "lazy" }, otherProps, { ref: ref, style: imgStyle })));
});
//# sourceMappingURL=Image.js.map